// React import
import React, { ReactNode } from "react";
// Material UI components
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Box, Typography, IconButton } from "@mui/material";
// Internal library imports
import { Button } from "@int-traderjoes/ui-components-library";
// Context imports
import { useTheme } from "@/context";
// Styles
import { getModalPopupStyles, ModalBackgroundStyles } from "@/styles/sx";


interface ConfirmationBoxProps {
  open: boolean; // Controls modal visibility
  onClose: () => void; // Function to close the modal
  onConfirm?: () => void; // Function to handle confirm button click
  onDecline?: () => void;
  title?: string | ReactNode; // Modal title - can be string or JSX
  message: string | ReactNode; // Message content - can be string or JSX with custom formatting
  confirmText?: string; // Text for the confirm button
  cancelText?: string; // Text for the cancel button
  showCancelButton?: boolean; // Optionally display the cancel button
}

const ConfirmationBox: React.FC<ConfirmationBoxProps> = ({
  open,
  onClose,
  onConfirm,
  onDecline,
  title = "Confirmation",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  showCancelButton = true,
}) => {
  const { darkMode } = useTheme();

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 644,
    bgcolor: darkMode ? "transparent" : "#FFFFFF",
    boxShadow: 24,
  } as const;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p="16px"
          className="bg-TJ-Not-Quite-White dark:bg-TJ-Dark"
        >
          <Typography
            variant="h6"
            sx={getModalPopupStyles(darkMode)}
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon
              sx={{
                color: darkMode ? "#fff" : "#000",
              }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            backgroundColor: darkMode ? "#1E1E1E" : "#FFFFFF",
            p: "16px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Cerebri Sans",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "25px",
              color: darkMode ? "#F8F8F2" : "#2C2C2C",
            }}
          >
            {message}
          </Typography>
          <Box
            sx={{
              borderBottom: "1px solid #DEDEDE",
              mt: "16px",
            }}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          px="16px"
          py="24px"
          gap={2}
          sx={ModalBackgroundStyles(darkMode)}
        >
          {showCancelButton && (
            <Button
              className={darkMode ? "dark" : "light"}
              size="large"
              text={cancelText}
              onClick={onDecline}
            />
          )}

          <Button
            className={darkMode ? "dark" : "light"}
            size="large"
            text={confirmText}
            primary
            onClick={onConfirm}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmationBox;
